// types
import { Address as AddressType } from '@/lib/types/address';
import { FulfillmentBillingContact as FulfillmentBillingContactType } from '@/lib/types/fulfillmentBillingContact';
import { StripeElements } from '@stripe/stripe-js';
import { logError } from '@/lib/functions/logError';
import { Address } from '@/lib/types/address';
import { OrderPayloadPricing } from '@/lib/types/orderPayload';

interface CheckoutResponse {
  processing: boolean,
  showToast: boolean,
  toastMessage?: string,
  errors: boolean,
  paymentError: boolean,
  complete: boolean,
  message: string,
  data?: { [key: string]: unknown },
}

export interface CartItem {
  'quantity': number,
  'code': string
}

const runStage = (currentStage: number, stage: number): boolean => {
  if (currentStage == stage) return true;
  return false;
};

const setCheckOutStage = (stage: string) => {
  sessionStorage.setItem('checkoutSession', stage);
};

const normalizeOptionalString = (value?: string | null): string => (value ?? '').trim();

const normalizeProvinceCode = (value?: string | null): string => {
  const trimmed = normalizeOptionalString(value);
  if (!trimmed) {
    return '';
  }
  if (trimmed.startsWith('US-') || trimmed.startsWith('CA-')) {
    return trimmed;
  }
  if (trimmed.length === 2) {
    return `US-${trimmed}`;
  }
  return trimmed;
};

const normalizePhoneDigits = (value?: string | null): string =>
  (value ?? '').replace(/\D/g, '');

export const cleanUp = () => {
  setCheckOutStage('1');
  sessionStorage.removeItem('checkoutSessioninitiateData');
  sessionStorage.removeItem('checkoutSessionUserIntent');
};

export const handleCheckout = async (
  orderToken: string,
  shippingAddress: Address,
  paymentMethodCode: string,
  billingContact: FulfillmentBillingContactType,
  billingAddress: AddressType,
  formData: FormData,
  prices: { [key: string]: number | boolean | string | null | OrderPayloadPricing },
  paymentKey: string | null | undefined,
  sessionToken: string | null | undefined,
  sessionEmail: string | null | undefined,
  elements: StripeElements,
  stripe: unknown,
  shippingMethod: string | null | undefined,
  cartAddressed: boolean | null | undefined,
  orderHasShipping: boolean | null | undefined,
): Promise<CheckoutResponse> => {

  let checkoutSession = sessionStorage.getItem('checkoutSession') || '1';
  if (checkoutSession == '1' || checkoutSession == '4') {
    checkoutSession = '1';
    cleanUp();
    setCheckOutStage('1');
  }

  if (!orderToken) {
    return {
      processing: false,
      showToast: false,
      errors: true,
      paymentError: false,
      complete: false,
      message: 'Checkout failed, no token found.',
    };
  }

  const initialBody = {
    paymentMethodCode: paymentMethodCode,
    billingContact: billingContact,
    billingAddress: billingAddress,
    shippingAddress: shippingAddress,
    phoneNumber: formData.get('shippingPhoneNumber') as string,
    firstName: formData.get('shippingFirstName') as string,
    lastName: formData.get('shippingLastName') as string,
    prices: prices,
    paymentKey: paymentKey,
  };

  const apiUrl = `/api/orders/${orderToken}/initiate`;
  const initiateResponse = await fetch(apiUrl, {
    method: 'POST',
    cache: 'no-cache',
    body: JSON.stringify(initialBody),
  });
  const initiateData = await initiateResponse.json();

  if (!initiateResponse.ok) {
    setCheckOutStage('1');
    const message = initiateData.message ? initiateData.message : 'Checkout init failed.';

    logError('Could not initiate checkout', JSON.stringify({
      message: message,
      data: initiateData,
    }));

    return {
      processing: false,
      showToast: true,
      errors: true,
      paymentError: false,
      complete: false,
      message: message,
      data: initiateData?.data ?? null,
    };
  }

  let changes = initiateData.data.order.billing.city !== billingAddress.city ||
    initiateData.data.order.billing.firstName !== billingContact.firstName ||
    initiateData.data.order.billing.lastName !== billingContact.lastName ||
    normalizePhoneDigits(initiateData.data.order.billing.phone) !== normalizePhoneDigits(billingContact.phoneNumber) ||
    initiateData.data.order.billing.postcode !== billingAddress.postcode ||
    normalizeProvinceCode(initiateData.data.order.billing.provinceCode) !== normalizeProvinceCode(billingAddress.provinceCode) ||
    initiateData.data.order.billing.street !== billingAddress.street ||
    normalizeOptionalString(initiateData.data.order.billing.street2) !== normalizeOptionalString(billingAddress.street2);

  if (initiateData.data.order.fulfillment?.shippingAddress) {
    changes = changes ||
      initiateData.data.order.fulfillment.shippingAddress.firstName != shippingAddress.firstName ||
      initiateData.data.order.fulfillment.shippingAddress.lastName != shippingAddress.lastName ||
      initiateData.data.order.fulfillment.shippingAddress.phone != shippingAddress.phoneNumber;
  }

  const initPrices = initiateData.data.prices;
  delete initiateData.data.order;
  delete initiateData.data.prices;

  if (!initiateData.data) {
    logError('No data passed in to checkout.', JSON.stringify({
      message: 'Data is null.',
      data: initiateData,
    }));

    setCheckOutStage('1');
    cleanUp();
    return {
      processing: false,
      showToast: false,
      errors: true,
      paymentError: false,
      complete: false,
      message: 'No Data provided for checkout.',
    };
  }

  sessionStorage.setItem('checkoutSessioninitiateData', JSON.stringify(initiateData));
  checkoutSession = '2';
  setCheckOutStage('2');

  const { error: submitError } = await elements.submit();
  if (submitError) {
    logError('Stripe could not submit elements.', JSON.stringify({
      message: submitError?.message,
      data: { orderToken: orderToken },
    }));

    cleanUp();
    return {
      processing: false,
      showToast: false,
      errors: true,
      paymentError: true,
      complete: false,
      message: 'Failed to submit payment elements.',
    };
  }

  checkoutSession = '3';
  setCheckOutStage('3');

  if (runStage(Number(checkoutSession), 3)) {
    const billingPostCode = billingAddress.postcode;
    const key = initiateData?.data?.client_secret
      ? initiateData.data.client_secret
      : JSON.parse(sessionStorage.getItem('checkoutSessioninitiateData')!).data.client_secret;

    // @ts-expect-error stripe client typing
    const userIntent = await stripe.confirmPayment(
      {
        redirect: 'if_required',
        elements: elements,
        clientSecret: key,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              address: {
                postal_code: billingPostCode,
                country: 'US',
              },
            },
          },
          return_url: `${process.env.NEXT_PUBLIC_SITE_URL}/checkout`,
        },
      },
      {
        idempotencyKey: orderToken + 'cp',
      },
    );

    let skipSectionThree = false;
    if ('error' in userIntent) {
      if ('payment_intent' in userIntent.error) {
        if (userIntent.error.payment_intent.status === 'succeeded' || userIntent.error.payment_intent.status === 'requires_capture') {
          sessionStorage.setItem('checkoutSessionUserIntent', JSON.stringify(userIntent.error.payment_intent));
          checkoutSession = '4';
          setCheckOutStage('4');
          skipSectionThree = true;
        }
      }
      if (!skipSectionThree) {
        logError('Stripe intent failed to confirm payment.', JSON.stringify({
          message: userIntent?.error?.message,
          data: userIntent,
        }));
        cleanUp();
        return {
          processing: false,
          showToast: false,
          errors: true,
          paymentError: true,
          complete: false,
          message: 'Order did not complete. 1',
        };
      }
    } else {
      sessionStorage.setItem('checkoutSessionUserIntent', JSON.stringify(userIntent));
      checkoutSession = '4';
      setCheckOutStage('4');
    }
  }

  if (runStage(Number(checkoutSession), 4)) {
    const userIntent = sessionStorage.getItem('checkoutSessionUserIntent');
    const initData = sessionStorage.getItem('checkoutSessioninitiateData');
    if (!userIntent || !initData) {
      logError('Order did not complete, user intent / init Data is blank.', JSON.stringify({
        message: 'Either userIntent or initData is null.',
        data: '',
      }));
      cleanUp();
      return {
        processing: false,
        showToast: false,
        errors: true,
        paymentError: false,
        complete: false,
        message: 'Order did not complete. 2',
      };
    }

    const orderNotes = formData.get('orderNotes') as string;

    const finalizeBody = {
      paymentId: initiateData.data.paymentId,
      paymentMethodCode: paymentMethodCode,
      billingContact: billingContact,
      shippingAddress: shippingAddress,
      email: sessionEmail,
      billingAddress: billingAddress,
      phoneNumber: formData.get('shippingPhoneNumber') as string,
      firstName: formData.get('shippingFirstName') as string,
      lastName: formData.get('shippingLastName') as string,
      notes: formData.get('orderNotes') as string || '',
      prices: initPrices,
      paymentKey: paymentKey,
      shipmentId: JSON.parse(initData).data.shipmentId,
      userIntent: JSON.stringify(userIntent),
      paymentIntentId: JSON.parse(initData).data.paymentIntentId || '',
      orderNotes: orderNotes,
      selectedMethod: 'shipping',
      shippingMethod: shippingMethod,
      cartAddressed,
      orderHasShipping,
      changes,
    };

    const apiUrlFinal = `/api/orders/${orderToken}/finalize`;
    const finalizeResponse = await fetch(apiUrlFinal, {
      method: 'POST',
      cache: 'no-cache',
      body: JSON.stringify(finalizeBody),
    });

    if (!finalizeResponse.ok) {
      const errorData = await finalizeResponse.json();
      const errorMessage = errorData?.message || 'Order did not complete. finalization.';
      const errorType = errorData?.errorType;

      logError('Order did not complete:', JSON.stringify({
        message: errorMessage,
        data: errorData,
      }));
      cleanUp();

      return {
        processing: false,
        showToast: errorData?.showToast ?? false,
        toastMessage: errorMessage,
        errors: true,
        paymentError: false,
        complete: false,
        message: errorMessage,
        data: {
          errorType: errorType,
        },
      };
    }

    const finalizeData = await finalizeResponse.json();

    cleanUp();

    return {
      processing: false,
      showToast: false,
      errors: false,
      paymentError: false,
      complete: true,
      message: 'Complete',
      data: {
        orderData: finalizeData.data,
        paymentIntentId: initiateData.data.paymentIntentId || '',
      },
    };
  }

  return {
    processing: false,
    showToast: false,
    errors: true,
    paymentError: false,
    complete: false,
    message: 'Order failed.',
  };
};
